// @ts-nocheck
/**
 * Edit SMS Webhook — redesigned to obd3-gui pattern
 * (ScrollPage + PageHeader + Card sections + PageLoader on initial load).
 * Backend quirk preserved: request_type "POST" is sent as "WHATSAPP".
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { PageLoader } from '@/components/feedback/PageLoader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_SMSWEBHOOK_GET_ONE, API_SMSWEBHOOK_UPDATE } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import ApiBuilder from './components/ApiBuilder';
import { isWebhookUrl } from './components/apiBuilderUtils';
import CopyButton from './components/CopyButton';

const EditSmsWebhook = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [webhook_name, setWebhookName] = useState('');
  const [request_type, setRequestType] = useState('');
  const [url, setUrl] = useState('');
  const [sms_text, setSmsText] = useState('');
  const [payload, setPayload] = useState('');
  const [header, setHeader] = useState('');

  const apiMethod = useMemo(() => request_type || 'GET', [request_type]);

  const formattedHeader = useMemo(() => {
    const h = (header || '').trim();
    if (!h) return '';
    try {
      return JSON.stringify(JSON.parse(h), null, 2);
    } catch {
      return h;
    }
  }, [header]);

  const initialHeaders = useMemo(() => {
    const h = (header || '').trim();
    if (!h) return [{ key: '', value: '' }];
    try {
      const parsed = JSON.parse(h);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return [{ key: '', value: '' }];
      }
      const arr = Object.entries(parsed).map(([k, v]) => ({
        key: String(k),
        value: typeof v === 'string' ? v : JSON.stringify(v),
      }));
      return arr.length ? arr : [{ key: '', value: '' }];
    } catch {
      return [{ key: '', value: '' }];
    }
  }, [header]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resp: any = await getApiCall(API_SMSWEBHOOK_GET_ONE.replace(':id', id), {});
        setWebhookName(resp?.webhook_name || '');
        setRequestType(resp?.request_type === 'WHATSAPP' ? 'POST' : resp?.request_type || '');
        setUrl(resp?.url || '');
        setSmsText(resp?.sms_text || '');
        setPayload(resp?.payload || '');
        setHeader(resp?.header || '');
      } catch (err: any) {
        toast.error(friendlyApiError(err) || 'Failed to load webhook');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  const validate = () => {
    const name = webhook_name.trim();
    const u = url.trim();
    const type = request_type;

    if (!name || !u || !type) {
      toast.error('Please fill all required fields.');
      return false;
    }
    if (!isWebhookUrl(u)) {
      toast.error('Please enter a valid Webhook URL (must start with http:// or https://).');
      return false;
    }
    if (header && header.trim()) {
      try {
        JSON.parse(header);
      } catch {
        toast.error('Headers must be valid JSON (use Api Builder headers section).');
        return false;
      }
    }
    if (type === 'GET' && !sms_text.trim()) {
      toast.error('SMS Text is required for GET requests.');
      return false;
    }
    if (type === 'POST' && !payload.trim()) {
      toast.error('Payload is required for POST requests.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const submitWebhook = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const apiRequestType = request_type === 'POST' ? 'WHATSAPP' : 'GET';
    const body = {
      webhook_name: webhook_name.trim(),
      url: url.trim(),
      request_type: apiRequestType,
      sms_text: request_type === 'GET' ? sms_text : null,
      payload: request_type === 'POST' ? payload : null,
      header: header && header.trim() ? header.trim() : null,
      status: '1',
    };
    try {
      await postApiCall(API_SMSWEBHOOK_UPDATE.replace(':id', id), body);
      toast.success('Webhook updated successfully!');
      navigate(`/${ROUTES.SMSWEBHOOK.LIST}`);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const handleApiBuilderChange = (apiState: any) => {
    setUrl(apiState?.url || '');
    if (!apiState) {
      setHeader('');
      return;
    }
    if (apiState.headerMode === 'raw') {
      setHeader((apiState.rawHeaders || '').trim());
      return;
    }
    const headerObj = (apiState.headers || []).reduce((acc: any, h: any) => {
      const key = (h?.key || '').trim();
      if (!key) return acc;
      acc[key] = h?.value ?? '';
      return acc;
    }, {});
    setHeader(Object.keys(headerObj).length ? JSON.stringify(headerObj) : '');
  };

  const handleUseUrl = (u: string) => setUrl(u || '');
  const handleBuilderResponse = (bodyString: string) => setPayload(bodyString || '');

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <PageHeader
        title="Edit SMS"
        description="Update the outbound SMS/WhatsApp callback configuration."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.SMSWEBHOOK.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back to webhooks
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
            <CardDescription>Name and HTTP method.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="webhook_name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="webhook_name"
                required
                type="text"
                placeholder="Webhook Name"
                value={webhook_name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[A-Za-z]*$/.test(value)) setWebhookName(value);
                }}
                autoComplete="off"
                title="Only letters allowed."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="request_type">
                Method <span className="text-destructive">*</span>
              </Label>
              <Select value={request_type} onValueChange={setRequestType}>
                <SelectTrigger id="request_type">
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {request_type && (
          <Card>
            <CardHeader>
              <CardTitle>Request builder</CardTitle>
              <CardDescription>Adjust URL and headers, then Test.</CardDescription>
            </CardHeader>
            <CardContent>
              <ApiBuilder
                initialUrl={url}
                initialMethod={apiMethod}
                initialHeaders={initialHeaders}
                onUseUrl={handleUseUrl}
                onMethod={(m: any) => {
                  const next = String(m || '').toUpperCase() === 'GET' ? 'GET' : 'POST';
                  setRequestType(next);
                }}
                onResponse={handleBuilderResponse}
                onStateChange={handleApiBuilderChange}
              />
            </CardContent>
          </Card>
        )}

        {request_type === 'GET' && (
          <Card>
            <CardHeader>
              <CardTitle>SMS Text</CardTitle>
              <CardDescription>
                Message body sent when the webhook fires. Required for GET webhooks.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="sms_text">
                  Message <span className="text-destructive">*</span>
                </Label>
                <CopyButton text={sms_text} />
              </div>
              <Textarea
                id="sms_text"
                required
                value={sms_text}
                rows={6}
                className="!bg-muted/40 !shadow-none !border-0 font-mono text-xs text-foreground"
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\s+$/.test(value)) return;
                  setSmsText(value);
                }}
                onBlur={(e) => setSmsText(e.target.value.trim())}
              />
            </CardContent>
          </Card>
        )}

        {request_type === 'POST' && (
          <Card>
            <CardHeader>
              <CardTitle>Payload</CardTitle>
              <CardDescription>
                Replace the mobile number with{' '}
                <code className="rounded bg-muted px-1 text-xs">{'{PHONE}'}</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="payload">
                  JSON payload <span className="text-destructive">*</span>
                </Label>
                <CopyButton text={payload} />
              </div>
              <Textarea
                id="payload"
                required
                value={payload}
                rows={6}
                className="!bg-muted/40 !shadow-none !border-0 font-mono text-xs text-foreground"
                onChange={(e) => setPayload(e.target.value)}
              />
            </CardContent>
          </Card>
        )}

        {header && header.trim() && (
          <Card>
            <CardHeader>
              <CardTitle>Headers</CardTitle>
              <CardDescription>
                Read-only view of the headers configured in the request builder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-end">
                <CopyButton text={formattedHeader} />
              </div>
              <Textarea
                name="headers"
                value={formattedHeader}
                disabled
                rows={5}
                className="!bg-muted/40 !shadow-none !border-0 font-mono text-xs text-foreground"
              />
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.SMSWEBHOOK.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Confirm submission"
        description={
          <>
            Is the{' '}
            <span className="font-medium text-foreground">
              {request_type === 'POST' ? 'payload' : 'SMS text'}
            </span>{' '}
            correct?
          </>
        }
        confirmLabel="Yes, save"
        onConfirm={submitWebhook}
      />
    </ScrollPage>
  );
};

export default EditSmsWebhook;
